import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useAppSelector } from '../../app/hooks';
import useHypothesisFrames from '../../hooks/useHypothesisFrames.hooks';
import useAgentCartSync from '../../hooks/useAgentCartSync.hooks';
import useExpressTimer from '../../hooks/useExpressTimer.hooks';
import useUnresolvedItemsSummary from '../../hooks/useUnresolvedItemsSummary.hooks';
import useSessionStatus from '../../hooks/sessionStatus/useSessionStatus.hooks';
import { ICartItemRefs, SetCartItemRef } from '../../types';
import Colors from '../../utils/color';
import { selectFeatureFlagFinalOrderValidation } from '../../redux/features/config/config.selector';
import FinalOrderBackdrop from '../FinalOrderBackdrop';
import {
  DEFAULT_PANE_SIZES,
  MAX_PANE_SIZE,
  MIN_PANE_SIZE,
} from '../Layout/LayoutPreferenceDialog';
import AgentInterceptionConfirm from './AgentInterceptionConfirm';
import BottomActionBar from './BottomActionBar';
import CenterPane from './CenterPane';
import LeftSidebar from './LeftSidebar';
import RightSidebar from './RightSidebar';
import useAgentResponseTimeMetric from './hooks/useAgentResponseTimeMetric.hooks';
import useMenuTimePeriodRollover from './hooks/useMenuTimePeriodRollover.hooks';
import usePaneLayout from './hooks/usePaneLayout.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  page: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.porcelain,
  },
  topRow: {
    flex: 1,
    minHeight: 0,
    // No outer padding — the three panes attach edge-to-edge and to the app bar;
    // the PanelGroup fills this box. Positioning context for the Live Transcript
    // takeover.
    position: 'relative',
  },
  // Hairline divider between panes. The handle itself is a 1px rule with NO
  // white gutter, so adjacent pane content (e.g. the Transcript header band)
  // butts straight up against the line. The drag hit area (a wider invisible
  // ::before) and the grip pill (::after, grows + turns blue on hover/drag) live
  // in pseudo-elements so the visible line stays 1px while staying easy to grab.
  resizeHandle: {
    position: 'relative',
    zIndex: 1,
    width: 1,
    flexShrink: 0,
    outline: 'none',
    backgroundColor: Colors.alto,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: spacing(-0.75),
      right: spacing(-0.75),
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1,
      width: 3,
      height: spacing(3.5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease, height 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
      height: spacing(5),
    },
  },
  rightPane: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
}));

/**
 * MainPageLayout — new HITL terminal layout (replacement for MainContent).
 *
 * The three panes are resizable (react-resizable-panels); sizes persist per
 * agent via `autoSaveId`, and the "Layout preference" dialog drives them
 * imperatively. The top app bar (Header) lives outside this component (App.tsx).
 *
 * This component is the composition root only: the three panes are LeftSidebar /
 * CenterPane / the Agent Cart, and the stateful wiring (pane persistence, the
 * takeover-overlay width measurement, day-part menu rollover) lives in the
 * co-located hooks under ./hooks.
 */
const MainPageLayout = () => {
  const classes = useStyles();
  const cartItemRefs = useRef<ICartItemRefs>({});
  const setCartItemRef: SetCartItemRef = (cartItemId, ref) => {
    cartItemRefs.current[cartItemId] = ref;
  };

  // Resizable-pane wiring. The Layout preference dialog is hosted by
  // LayoutPreferenceProvider (App subtree); here we feed it live sizes, register
  // the imperative resizer, and persist manual drags (see usePaneLayout).
  const {
    paneStorageKey,
    panelGroupRef,
    reportLiveSizes,
    persistLayoutOnDragEnd,
  } = usePaneLayout();

  const FEATURE_FLAG_FINAL_ORDER_VALIDATION = useAppSelector(
    selectFeatureFlagFinalOrderValidation
  );

  // Poll the clock to roll the menu over between day-parts (breakfast/lunch/…).
  useMenuTimePeriodRollover();

  // Core call-handling side effects (cart rebuild from hypotheses, unresolved
  // items summary, session lifecycle). These run for their effects only.
  useHypothesisFrames();
  useUnresolvedItemsSummary();
  useSessionStatus();
  useAgentCartSync();
  useExpressTimer();
  // Emits agent_metric (agent_response_time) when a turn's response time exceeds
  // the configured limit during a takeover. Mounted here so it fires regardless
  // of the Pulse card's collapse state.
  useAgentResponseTimeMetric();

  return (
    <div id="main-page-layout" className={classes.page}>
      <div className={classes.topRow}>
        {/* No autoSaveId here on purpose: the horizontal split persists solely
            through the layout-preference key (drags via persistLayoutOnDragEnd,
            the dialog via Apply), so there's a single source of truth. */}
        <PanelGroup
          direction="horizontal"
          ref={panelGroupRef}
          onLayout={reportLiveSizes}
        >
          {/* LEFT SIDEBAR: one card — transcript + reminder sections + AI status. */}
          <Panel
            id="left"
            order={1}
            defaultSize={DEFAULT_PANE_SIZES[0]}
            minSize={MIN_PANE_SIZE}
            maxSize={MAX_PANE_SIZE}
            style={{ overflow: 'hidden' }}
          >
            <LeftSidebar paneStorageKey={paneStorageKey} />
          </Panel>

          <PanelResizeHandle
            className={classes.resizeHandle}
            onDragging={persistLayoutOnDragEnd}
          />

          {/* MIDDLE: one card — current step (top) + option groups (ItemBuilder) */}
          <Panel
            id="middle"
            order={2}
            defaultSize={DEFAULT_PANE_SIZES[1]}
            minSize={MIN_PANE_SIZE}
            maxSize={MAX_PANE_SIZE}
          >
            <CenterPane setCartItemRef={setCartItemRef} />
          </Panel>

          <PanelResizeHandle
            className={classes.resizeHandle}
            onDragging={persistLayoutOnDragEnd}
          />

          {/* RIGHT: agent cart + totals + order actions */}
          <Panel
            id="right"
            order={3}
            defaultSize={DEFAULT_PANE_SIZES[2]}
            minSize={MIN_PANE_SIZE}
            maxSize={MAX_PANE_SIZE}
          >
            <div className={classes.rightPane}>
              <RightSidebar paneStorageKey={paneStorageKey} />
            </div>
          </Panel>
        </PanelGroup>
      </div>

      {/* BOTTOM: quick response + conversation + order flow + escalation */}
      <BottomActionBar />

      {/* Take-over confirmation for stepping into a live auto-AI order. The Pulse
          status band that replaced AIStatusBar here is display-only, so this
          re-mounts the guard AIStatusBar hosts in the legacy layout. */}
      <AgentInterceptionConfirm />

      {FEATURE_FLAG_FINAL_ORDER_VALIDATION && <FinalOrderBackdrop />}
    </div>
  );
};

export default MainPageLayout;
